"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/providers/i18n";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

type GlbViewerProps = {
  url: string;
  className?: string;
  /** When false, disable OrbitControls and pointer interaction (so node dragging still works). */
  interactive?: boolean;
  /** Show mouse controls hint overlay (only meaningful when interactive=true). */
  showControlsHint?: boolean;
};

function disposeObject3D(root: THREE.Object3D) {
  try {
    root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      const geo = (mesh as any).geometry as THREE.BufferGeometry | undefined;
      const mat = (mesh as any).material as THREE.Material | THREE.Material[] | undefined;

      if (geo) geo.dispose?.();
      if (mat) {
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          try {
            const anyMat = m as any;
            // Common texture slots
            const texKeys = [
              "map",
              "alphaMap",
              "aoMap",
              "bumpMap",
              "displacementMap",
              "emissiveMap",
              "envMap",
              "lightMap",
              "metalnessMap",
              "normalMap",
              "roughnessMap",
            ];
            for (const k of texKeys) {
              const tex = anyMat?.[k] as THREE.Texture | undefined;
              if (tex) tex.dispose?.();
            }
          } catch {}
          m.dispose?.();
        }
      }
    });
  } catch {}
}

export function GlbViewer({
  url,
  className,
  interactive = true,
  showControlsHint = true,
}: GlbViewerProps) {
  const { t } = useI18n();
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let stopped = false;
    let raf = 0;
    let cleanupResize: (() => void) | undefined;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b0b0f);

    const camera = new THREE.PerspectiveCamera(50, 1, 0.01, 2000);
    camera.position.set(2.4, 1.6, 2.4);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // Use a wrapper so we can safely remove only our canvas.
    const canvas = renderer.domElement;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    // If this is just a preview inside a draggable node, don't intercept pointer events.
    if (!interactive) canvas.style.pointerEvents = "none";
    mount.appendChild(canvas);

    const ambient = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambient);
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(4, 8, 4);
    scene.add(dir);

    // Note: keep preview clean (no grid helper).

    const controls = interactive ? new OrbitControls(camera, canvas) : null;
    if (controls) {
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.screenSpacePanning = true;
      controls.enablePan = true;
      controls.enableZoom = true;
      controls.rotateSpeed = 0.7;
      controls.zoomSpeed = 0.9;
      controls.panSpeed = 0.7;
    }

    const resize = () => {
      try {
        const { clientWidth: w, clientHeight: h } = mount;
        if (!w || !h) return;
        renderer.setSize(w, h, false);
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
      } catch {}
    };

    // Prefer ResizeObserver for the node surface resizing.
    try {
      const ro = new ResizeObserver(() => resize());
      ro.observe(mount);
      cleanupResize = () => ro.disconnect();
    } catch {
      // Fallback
      window.addEventListener("resize", resize);
      cleanupResize = () => window.removeEventListener("resize", resize);
    }
    resize();

    // Prevent ReactFlow / page zoom from hijacking wheel (only when interactive).
    const onWheel = (e: WheelEvent) => {
      try {
        e.stopPropagation();
      } catch {}
    };
    if (interactive) {
      canvas.addEventListener("wheel", onWheel, { passive: true });
    }

    const loader = new GLTFLoader();
    const group = new THREE.Group();
    scene.add(group);

    setStatus("loading");
    loader.load(
      url,
      (gltf) => {
        try {
          if (stopped) return;
          group.add(gltf.scene);

          // Fit view: center model at origin and adjust camera distance.
          const box = new THREE.Box3().setFromObject(gltf.scene);
          const size = new THREE.Vector3();
          const center = new THREE.Vector3();
          box.getSize(size);
          box.getCenter(center);
          gltf.scene.position.sub(center); // center at origin

          const maxDim = Math.max(size.x, size.y, size.z);
          const safeMax = Number.isFinite(maxDim) && maxDim > 0 ? maxDim : 1;
          const distance = safeMax * 1.8;
          camera.position.set(distance, distance * 0.75, distance);
          if (controls) {
            controls.target.set(0, 0, 0);
            controls.update();
          } else {
            camera.lookAt(0, 0, 0);
          }
          setStatus("ready");
        } catch {
          setStatus("error");
        }
      },
      undefined,
      () => {
        if (!stopped) setStatus("error");
      }
    );

    const tick = () => {
      if (stopped) return;
      try {
        controls?.update();
        renderer.render(scene, camera);
      } catch {}
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      stopped = true;
      try {
        window.cancelAnimationFrame(raf);
      } catch {}
      try {
        if (interactive) canvas.removeEventListener("wheel", onWheel as any);
      } catch {}
      try {
        cleanupResize?.();
      } catch {}
      try {
        disposeObject3D(group);
      } catch {}
      try {
        controls?.dispose();
      } catch {}
      try {
        renderer.dispose();
      } catch {}
      try {
        mount.removeChild(canvas);
      } catch {}
    };
  }, [url, interactive]);

  return (
    <div ref={mountRef} className={cn("relative h-full w-full", className)}>
      {interactive && showControlsHint ? (
        <div className="pointer-events-none absolute bottom-2 left-2 z-10">
          <div className="rounded-xl border border-neutral-800 bg-neutral-950/60 px-3 py-2 text-[11px] text-white/80 backdrop-blur">
            {t('Left-drag rotate · Right-drag pan · Scroll zoom')}
          </div>
        </div>
      ) : null}
      {status === "loading" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded-xl border border-neutral-800 bg-neutral-950/60 px-3 py-2 text-xs text-white/80 backdrop-blur">
            Loading 3D…
          </div>
        </div>
      ) : null}
      {status === "error" ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded-xl border border-red-500/30 bg-neutral-950/60 px-3 py-2 text-xs text-red-200 backdrop-blur">
            Failed to load 3D model
          </div>
        </div>
      ) : null}
    </div>
  );
}

